import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircleIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import publicClient from '../../api/publicClient';

const CATEGORY_HEADERS = {
  work_style: {
    label: 'Work Style & Personality',
    hint: 'For each set, pick ONE statement MOST like you and ONE LEAST like you.',
  },
  values: {
    label: 'Values & Workplace Tendencies',
    hint: 'Choose the statement closest to your view.',
  },
  behavior: {
    label: 'Real-World Behavioral Reflection',
    hint: 'Choose the option that best reflects your actual tendencies in past workplaces.',
  },
  judgment: {
    label: 'Judgment Scenarios',
    hint: 'For ranking questions, drag (or use the arrows) so 1 is what you would actually do first.',
  },
};

function isAnswerComplete(question, answer) {
  if (!question.is_required) return true;
  switch (question.question_type) {
    case 'forced_choice':
      // Must be two DIFFERENT options. We compare by the option's
      // visible text — profile tags are an admin-only field, and some
      // seeded questions have two options that share the same profile
      // tag intentionally. The old check (profiles must differ) would
      // strand a candidate forever on those questions with no way out.
      return !!(answer?.most && answer?.least
                && (answer.most.text || answer.most.profile)
                && (answer.least.text || answer.least.profile)
                && (answer.most.text || answer.most.profile) !== (answer.least.text || answer.least.profile));
    case 'radio':
      // options are now objects {text, profile}, so answer is an object too
      return !!(answer && typeof answer === 'object' && answer.text);
    case 'ranking':
      return Array.isArray(answer) && answer.length === (question.options?.length || 0);
    case 'likert':
      return typeof answer === 'number' && answer >= 1 && answer <= 5;
    case 'checkbox':
      return Array.isArray(answer) && answer.length > 0;
    default:
      return typeof answer === 'string' && answer.trim().length > 0;
  }
}

function intro({ firstName }) {
  return (
    <div className="bg-gray-800/40 border border-gray-700/50 rounded-2xl p-5 mb-6 text-sm text-gray-300 leading-relaxed">
      <p className="mb-3">
        Thank you{firstName ? `, ${firstName}` : ''}, for applying to Bright Beginnings.
      </p>
      <p className="mb-3">
        We believe great childcare takes more than experience — it takes emotional intelligence, accountability, communication, reliability, and a genuine commitment to children and families. This assessment helps us better understand how you naturally work, communicate, and make decisions in real-world environments.
      </p>
      <p className="text-gray-400">
        There are no perfect answers — please answer honestly based on what is most true for you. Should take 8–10 minutes.
      </p>
    </div>
  );
}

export default function SurveyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // After submit-success, auto-forward to /schedule/:token after a short
  // confirmation pause so the candidate sees the "thanks" beat before the
  // page changes underneath them.
  useEffect(() => {
    if (!submitted) return;
    const t = setTimeout(() => { navigate(`/schedule/${token}`); }, 1800);
    return () => clearTimeout(t);
  }, [submitted, token, navigate]);

  useEffect(() => {
    let cancelled = false;
    publicClient.get(`/api/survey/${token}`)
      .then(({ data }) => {
        if (cancelled) return;
        setCandidate(data.candidate);
        setQuestions(data.questions);
        // Pre-seed ranking answers with a RANDOMIZED starting order — if we
        // used the migration's option order, candidates who just click
        // submit would skew toward whichever profile happens to sit at rank 1.
        // Randomizing forces an intentional reorder.
        const seed = {};
        for (const q of data.questions) {
          if (q.question_type === 'ranking' && Array.isArray(q.options)) {
            const shuffled = q.options.slice();
            for (let i = shuffled.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            seed[q.id] = shuffled;
          }
        }
        if (Object.keys(seed).length) setAnswers(seed);
      })
      .catch(err => {
        if (cancelled) return;
        if (err.response?.data?.completed) setError('completed');
        else setError('invalid');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const setAnswer = (qid, value) => setAnswers(prev => ({ ...prev, [qid]: value }));

  const progress = useMemo(() => {
    if (questions.length === 0) return 0;
    const done = questions.filter(q => isAnswerComplete(q, answers[q.id])).length;
    return Math.round((done / questions.length) * 100);
  }, [questions, answers]);

  const groupedQuestions = useMemo(() => {
    const groups = {};
    for (const q of questions) {
      const key = q.category in CATEGORY_HEADERS ? q.category : 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(q);
    }
    return groups;
  }, [questions]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const missing = questions.find(q => !isAnswerComplete(q, answers[q.id]));
    if (missing) {
      toast.error('Please answer every required question before submitting.');
      const el = document.getElementById(`q-${missing.id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    try {
      const responses = questions.map(q => ({
        question_id: q.id,
        question_display_order: q.display_order,
        question_text: q.question_text,
        question_type: q.question_type,
        answer: answers[q.id] ?? null,
      }));
      await publicClient.post(`/api/survey/${token}`, { responses });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-gray-400">Loading questionnaire...</div></div>;
  }

  if (error === 'completed') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Already Completed</h2>
          <p className="text-gray-400">You've already submitted your questionnaire. Our team will be in touch soon!</p>
        </div>
      </div>
    );
  }

  if (error === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Invalid Link</h2>
          <p className="text-gray-400">This survey link is invalid or has expired. Please contact us at (574) 855-1604 if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-6">
        <Toaster position="top-right" />
        <div className="max-w-md text-center">
          <div className="bg-green-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Thanks, {candidate?.first_name}!</h2>
          <p className="text-gray-400 text-lg mb-2">We've got your responses.</p>
          <p className="text-gray-500">Our team will review and reach out about next steps.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <Toaster position="top-right" />

      <div className="sticky top-0 z-30 -mx-6 px-6 pb-3 pt-2 bg-gray-900/95 backdrop-blur border-b border-gray-800">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
            <span>Talent &amp; Culture Assessment</span>
            <span>{progress}% complete</span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div className="h-full bg-orange-500 transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto pt-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-xl">BB</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Bright Beginnings Talent &amp; Culture Assessment</h1>
        </div>

        {intro({ firstName: candidate?.first_name })}

        <form onSubmit={onSubmit} className="space-y-8">
          {Object.entries(groupedQuestions).map(([cat, qs]) => {
            const header = CATEGORY_HEADERS[cat] || { label: 'More questions', hint: '' };
            return (
              <div key={cat} className="space-y-3">
                <div className="flex items-baseline justify-between mb-1">
                  <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{header.label}</h2>
                </div>
                {header.hint && <p className="text-sm text-gray-500 -mt-1 mb-2">{header.hint}</p>}

                {qs.map(q => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    answer={answers[q.id]}
                    setAnswer={(v) => setAnswer(q.id, v)}
                  />
                ))}
              </div>
            );
          })}

          {/* When the form isn't 100% complete, the click runs onSubmit's
              "missing question" branch — it toasts an error AND scrolls to
              the first incomplete question. Previously the button was
              `disabled` and the candidate had no way to find out which
              question was unfinished, so they sat at "96%" indefinitely.
              Most common cause: a forced-choice question where the same
              option was picked for both "most like me" and "least like
              me" (those have to differ). */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
          >
            {submitting
              ? 'Submitting…'
              : progress < 100
                ? `Find what's still unfinished (${progress}% done)`
                : 'Submit Questionnaire'}
          </button>
          {progress < 100 && (
            <p className="text-center text-xs text-gray-500 mt-2 mb-6">
              Tap the orange button and we'll scroll you straight to the question that still needs an answer. The most common one: a "most like me / least like me" pair where the same option got picked for both — those need to be different.
            </p>
          )}
        </form>

        <p className="text-center text-gray-600 text-sm mt-6">
          Bright Beginnings Childcare — Confidential
        </p>
      </div>
    </div>
  );
}

function QuestionCard({ question, answer, setAnswer }) {
  return (
    <div id={`q-${question.id}`} className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-5">
      <p className="text-white font-medium mb-4">{question.question_text}</p>

      {question.question_type === 'forced_choice' && (
        <ForcedChoice options={question.options || []} value={answer} onChange={setAnswer} />
      )}
      {question.question_type === 'radio' && (
        <RadioGroup options={question.options || []} value={answer} onChange={setAnswer} />
      )}
      {question.question_type === 'ranking' && (
        <RankingList options={question.options || []} value={answer} onChange={setAnswer} />
      )}
      {(question.question_type === 'text' || question.question_type === 'textarea') && (
        <textarea
          rows={question.question_type === 'textarea' ? 3 : 1}
          value={answer || ''}
          onChange={(e) => setAnswer(e.target.value)}
          className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 resize-none"
          placeholder="Your answer..."
        />
      )}
    </div>
  );
}

function ForcedChoice({ options, value, onChange }) {
  const safeValue = value || {};
  const setPick = (kind, opt) => {
    const next = { ...safeValue, [kind]: { profile: opt.profile, text: opt.text } };
    const other = kind === 'most' ? 'least' : 'most';
    if (next[other]?.profile === opt.profile && next[other]?.text === opt.text) {
      delete next[other];
    }
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 text-xs text-gray-500 px-3">
        <div></div>
        <div className="w-14 text-center">Most</div>
        <div className="w-14 text-center">Least</div>
      </div>
      {options.map((opt, idx) => {
        const isMost = safeValue.most?.text === opt.text;
        const isLeast = safeValue.least?.text === opt.text;
        return (
          <div key={idx} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center bg-gray-700/30 border border-gray-600/40 rounded-lg px-3 py-2">
            <span className="text-gray-200 text-sm">{opt.text}</span>
            <button
              type="button"
              onClick={() => setPick('most', opt)}
              className={`w-14 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                isMost
                  ? 'bg-green-500/20 border-green-400 text-green-300'
                  : 'bg-gray-800/40 border-gray-600 text-gray-400 hover:border-gray-500'
              }`}
            >Most</button>
            <button
              type="button"
              onClick={() => setPick('least', opt)}
              className={`w-14 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                isLeast
                  ? 'bg-red-500/20 border-red-400 text-red-300'
                  : 'bg-gray-800/40 border-gray-600 text-gray-400 hover:border-gray-500'
              }`}
            >Least</button>
          </div>
        );
      })}
    </div>
  );
}

function RadioGroup({ options, value, onChange }) {
  // options are objects { text, profile }. The answer is the picked option
  // object — we keep both fields so the admin renderer + scorer don't have
  // to look up options again.
  return (
    <div className="space-y-2">
      {options.map((opt, idx) => {
        const optObj = typeof opt === 'string' ? { text: opt, profile: null } : opt;
        const isChecked = value?.text === optObj.text;
        return (
          <label key={idx} className={`flex items-center gap-3 cursor-pointer border rounded-lg px-3 py-2 transition-colors ${
            isChecked ? 'bg-orange-500/10 border-orange-400/50' : 'bg-gray-700/30 border-gray-600/40 hover:border-gray-500'
          }`}>
            <input
              type="radio"
              checked={isChecked}
              onChange={() => onChange(optObj)}
              className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
            />
            <span className="text-gray-200 text-sm">{optObj.text}</span>
          </label>
        );
      })}
    </div>
  );
}

function RankingList({ options, value, onChange }) {
  // value is the candidate's current ordering (array of option objects).
  // We pre-seed it to the canonical option order on first render so they
  // only have to nudge things, not start from empty. Each row shows its
  // current rank number; up/down arrows swap with the neighbor.
  const list = Array.isArray(value) && value.length === options.length ? value : options;

  const swap = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = list.slice();
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {list.map((opt, idx) => (
        <div key={`${opt.text}-${idx}`} className="flex items-center gap-3 bg-gray-700/30 border border-gray-600/40 rounded-lg px-3 py-2">
          <div className="w-7 h-7 rounded-full bg-orange-500/20 text-orange-300 text-sm font-bold flex items-center justify-center flex-shrink-0">
            {idx + 1}
          </div>
          <span className="flex-1 text-gray-200 text-sm">{opt.text}</span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => swap(idx, -1)}
              disabled={idx === 0}
              className="p-1.5 rounded-md bg-gray-800/50 border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Move up"
            >
              <ArrowUpIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => swap(idx, +1)}
              disabled={idx === list.length - 1}
              className="p-1.5 rounded-md bg-gray-800/50 border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Move down"
            >
              <ArrowDownIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      <p className="text-xs text-gray-500 mt-1">1 = most likely, {list.length} = least likely</p>
    </div>
  );
}
